import React from "react"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { IBlogPost, IPostAssetReference } from "./types"
import { documentToHtmlString } from "@contentful/rich-text-html-renderer"
import ReactPlayer from "react-player"

/** Converts a contentful body to JSX
 * @param {IBlogPost["body"]} body
 * @returns {React.ReactNode} JSX elements
 */
export const convertBodyToJSX = (body: IBlogPost["body"]) => {
  const options = body.references ? renderOptions(body.references) : undefined

  return documentToReactComponents(JSON.parse(body.raw), options)
}

/**
 * Transforms an array of asset references to render their appropriate JSX elements
 * @param {IPostAssetReference[]} references
 */
function renderOptions(references: IPostAssetReference[]) {
  // create an asset map
  const assetMap = new Map()

  // loop through the assets and add them to the map
  for (const asset of references) {
    assetMap.set(asset.contentful_id, asset)
  }

  return {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
        // find the asset in the assetMap by ID
        const asset = assetMap.get(node.data.target.sys.id)
        // render the asset accordingly
        return (
          <img
            src={asset.url}
            height={asset.height}
            width={asset.width}
            alt={asset.description}
          />
        )
      },

      [INLINES.HYPERLINK]: (node: any) => {
        // Check if link is a youtube video link, and render a player as appropriate
        // Or else, just render the defualt anchor tag
        if (isValidYoutubeLink(node.data.uri)) {
          return (
            <div className="video youtube">
              <ReactPlayer
                controls
                style={{
                  alignItems: "stretch",
                }}
                url={node.data.uri}
              />
            </div>
          )
        }

        return <a href={node.data.uri}>{node.content[0]?.value}</a>
      },
    },
  }
}

/** Converts a contentful body to HTML string
 * @param {IBlogPost["body"]} body
 * @returns {string} HTML string
 */
export const convertBodyToHTMLString = (body: IBlogPost["body"]): string => {
  const html = documentToHtmlString(JSON.parse(body.raw))

  // We're wrapping the html with a div tag so that the all the inner html elements
  // can have a single parent div element when we render them.
  // This is particularly important when we need to truncate the html content
  return `<div>${html}</div>`
}

/**
 * Checks if a url string is a valid youtube video link
 * @param {string} url
 * @returns {boolean} `true` or `false`
 */
export const isValidYoutubeLink = (url: string): boolean => {
  return /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/.test(
    url
  )
}
